import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ClipboardList, Play, Send, X } from "lucide-react";
import { formatMoney } from "../api";
import { useAuth } from "../auth";
import { useClientSelection } from "../client-context";
import { useApi } from "../hooks";
import ConnectionNotice from "../components/ConnectionNotice";
import { EmptyState, ErrorState, PageHeader, PageSkeleton, StatusBadge } from "../components/Ui";
import { NoClient, useMutation } from "../components/Workflow";
import {
  canUndo, evidence, failedChecks, findingAccount, hasSeveralAccounts, orderedGroups, reviewStatus, ruleParts,
  sopFailedText, sopStatusText, type Finding, type SopStep, type Week,
  shortDate,
} from "../fulfillment";

const GROUP = {
  ready: { title: "Ready to send to approvals", note: "Each one sends one exact change to Approvals. Approving it there applies it in Google Ads." },
  person: { title: "Needs a person", note: "There's no single exact change for these. Review the evidence, open the campaign in Campaigns, or make it a task." },
  handled: { title: "Handled", note: undefined },
} as const;
const UNDO_SHOWN_MS = 10_000; // the Undo toast; a dismissed row keeps its own Undo for an hour
/** Knowledge base titles by code ("KB-029" → its title), so a rule names its playbook. Empty until loaded, or when the list can't be read: codes show instead. */
const KbTitles = createContext<Record<string, string>>({});

/** The weekly Google Ads review for one client: what NOLA found, with the playbook rule behind each finding. */
export default function AdsReview() {
  const { clientId, client } = useClientSelection();
  return (
    <>
      <PageHeader
        title="Weekly review"
        description={clientId
          ? `Findings in ${client?.name || "this client"}'s Google Ads from the weekly audit. Nothing changes in Google Ads until a change is approved.`
          : "Pick a client in the sidebar."}
      />
      {clientId ? <Review key={clientId} clientId={clientId} /> : <NoClient />}
    </>
  );
}

type Handlers = {
  onQueue: () => void; onTask: () => void; onDismiss: () => void; onUndo: () => void; campaignsLink: string;
};

/**
 * The open findings in a group with no exact change, merged when they are the same finding:
 * same rule, title and detail ("No Customer Match list of past customers" once per account
 * read as three problems, each with its own buttons). A change for Approvals stays its own row.
 */
function sameFindings(items: Finding[]): Finding[][] {
  const out = new Map<string, Finding[]>();
  for (const f of items) {
    const key = f.status === "open" && !f.proposal ? `${f.kind}|${f.rule}|${f.title}|${f.detail ?? ""}` : `id:${f.id}`;
    out.set(key, [...(out.get(key) || []), f]);
  }
  return [...out.values()];
}
const CAMPAIGN_KINDS = new Set([
  "budget_lost", "rank_lost", "low_quality_score", "poor_ad_strength",
  "bidding_data", "broad_match", "low_budget", "ghost_campaign", "pmax", "ai_max", "brand_campaign",
]);

function Review({ clientId }: { clientId: number }) {
  const isAdmin = useAuth().user?.role === "superadmin";
  const week = useApi<Week>(`/api/fulfillment/${clientId}/week`);
  const kbList = useApi<{ doc_code: string; title: string }[]>("/api/kb");
  const kbTitles = useMemo(() => Object.fromEntries((kbList.data || []).filter((d) => d.title).map((d) => [d.doc_code.toUpperCase(), d.title])), [kbList.data]);
  const m = useMutation();
  // The findings just dismissed (one, or a merged row's): the toast offers Undo for a few seconds.
  const [dismissed, setDismissed] = useState<number[] | null>(null);
  useEffect(() => {
    if (dismissed === null) return;
    const timer = window.setTimeout(() => setDismissed(null), UNDO_SHOWN_MS);
    return () => window.clearTimeout(timer);
  }, [dismissed]);
  if (week.loading && !week.data) return <PageSkeleton cards={2} />;
  if (week.error || !week.data) return <ErrorState message={week.error || "The review could not load."} onRetry={week.reload} />;
  const { run, findings, summary, sop = [] } = week.data;
  const groups = orderedGroups(findings);
  const openGroups = groups.filter((g) => g.key !== "handled").length;
  const running = run?.status === "running";
  const several = hasSeveralAccounts(run, findings);
  const failed = failedChecks(run, several);
  const busy = m.busy;
  // One path, or a merged row's paths in turn (the API acts on one finding at a time).
  const act = async (paths: string | string[], notice = "") => {
    setDismissed(null);
    const list = [paths].flat();
    let ok = true;
    for (const path of list) if ((await m.run(path, {})) === undefined) { ok = false; break; }
    if (ok && notice) m.setNotice(notice);
    if (list.some((path) => path.endsWith("/task"))) window.dispatchEvent(new Event("nl-aos-tasks-changed"));
    week.reload();
    return ok;
  };
  const handlers = (group: Finding[]): Handlers => {
    const f = group[0];
    const each = (verb: string) => group.map((x) => `/api/fulfillment/findings/${x.id}/${verb}`);
    return {
      campaignsLink: `/ads-campaigns?client_id=${clientId}&account=${f.customer_id}`,
      onQueue: () => void act(each("queue"), "Sent to Approvals."),
      onTask: () => void act(each("task"), group.length > 1 ? `Added ${group.length} tasks to your Work queue.` : "Added to your Work queue."),
      // No confirm box: it goes at once, and Undo brings it back.
      onDismiss: () => void act(each("dismiss")).then((ok) => { if (ok) setDismissed(group.map((x) => x.id)); }),
      onUndo: () => void act(each("reopen")),
    };
  };
  const runButton = (
    <button className="button" disabled={busy || running} onClick={() => void act(`/api/fulfillment/${clientId}/run`, "Review finished.")}>
      <Play aria-hidden="true" />{m.busy ? "Working…" : "Run review now"}
    </button>
  );

  return (
    <KbTitles.Provider value={kbTitles}>
      <ConnectionNotice clientId={clientId} feeds={["google_ads"]} />
      {run && (
        <div className="review-bar">
          <p className="muted">{reviewStatus(run)}</p>
          {runButton}
        </div>
      )}
      {m.feedback}
      {/* A failed review read nothing: "Nothing to fix this week" would be wrong. */}
      {run?.status === "failed"
        ? <p className="inline-notice warning" role="status">The last review didn't finish, so this week's findings may be missing. Press Run review now to try again.</p>
        : failed.length > 0 && <p className="inline-notice warning" role="status">Some checks could not be read: {failed.join("; ")}. The rest are below.</p>}
      {!run && week.data?.linked === false ? (
        <EmptyState
          title="No Google Ads account linked"
          description={isAdmin ? "Link this client's Google Ads account; the weekly review starts with the next nightly run." : "An administrator links accounts in Setup → Connections."}
          action={isAdmin ? <Link className="button secondary" to={`/client-settings?client_id=${clientId}`}>Open Connections</Link> : undefined}
        />
      ) : !run ? (
        <EmptyState title="No review yet" description="NOLA reviews each Google Ads account once a week. Run it now to see this week's findings." action={runButton} />
      ) : !findings.length ? (
        run.status === "failed" ? null : <EmptyState title="Nothing to fix this week" description="No step of the audit SOP found a problem in this week's review." />
      ) : (
        <>
          {/* Each group heading has its own count: the total only helps when two groups show. */}
          {(openGroups > 1 || summary.wasted_spend > 0) && (
            <p className="review-summary">
              {openGroups > 1 && <><strong>{summary.open}</strong> open{summary.wasted_spend > 0 && ", "}</>}
              {summary.wasted_spend > 0 && <><strong>{formatMoney(summary.wasted_spend)}</strong> spent with no conversions on the searches and placements below</>}
            </p>
          )}
          {groups.map((g) => (
            <Group key={g.key} title={GROUP[g.key].title} note={GROUP[g.key].note} items={g.items} busy={busy} handlers={handlers}
              account={(f) => (several ? findingAccount(f, run) : null)} />
          ))}
        </>
      )}
      {/* The audit SOP step by step: what the review found for each, and what still needs a person in Google Ads. */}
      {run && run.status !== "failed" && sop.length > 0 && <SopChecklist steps={sop} />}
      {dismissed !== null && !m.error && (
        <div className="undo-toast" role="status">
          <span>Dismissed.</span>
          <button type="button" className="text-button" disabled={busy} onClick={() => void act(dismissed.map((id) => `/api/fulfillment/findings/${id}/reopen`))}>Undo</button>
        </div>
      )}
    </KbTitles.Provider>
  );
}

function Group({ title, note, items, busy, handlers, account }: {
  title: string; note?: string; items: Finding[]; busy: boolean;
  handlers: (group: Finding[]) => Handlers; account: (f: Finding) => string | null;
}) {
  const rows = sameFindings(items);
  return (
    <section className="review-group" aria-label={title}>
      {/* The count is of rows: one finding on three accounts is one thing to do. */}
      <h2>{title} <span className="muted">{rows.length}</span></h2>
      {note && <p className="review-note">{note}</p>}
      <ul className="review-list">
        {rows.map((group) => {
          // A merged row names every account it covers (when the client has several).
          const names = [...new Set(group.map(account).filter((name): name is string => !!name))];
          return <Row key={group[0].id} f={group[0]} more={group.slice(1)} busy={busy} account={names.length ? joinNames(names) : null} {...handlers(group)} />;
        })}
      </ul>
    </section>
  );
}

/** "A, B and C". */
const joinNames = (names: string[]) => (names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0] || "");

function Row({ f, more = [], busy, account, onQueue, onTask, onDismiss, onUndo, campaignsLink }: { f: Finding; more?: Finding[]; busy: boolean; account: string | null } & Handlers) {
  const ev = evidence(f);
  const kbTitles = useContext(KbTitles);
  // A merged row: each finding's own evidence when they differ, else the shared line once.
  const lines = [f, ...more].map((x) => evidence(x).line).filter(Boolean);
  const merged = more.length > 0 && new Set(lines).size > 1;
  return (
    <li className="review-item">
      <div className="review-item-main">
        {/* Only High gets a badge: nine in ten findings are Medium, and a badge on every row says nothing. */}
        <div className="review-item-head">{[f, ...more].some((x) => x.severity === "high") && <StatusBadge tone="danger">High</StatusBadge>}<strong>{f.title}</strong></div>
        {f.detail && <p>{f.detail}</p>}
        {merged ? <ul className="review-evidence review-evidence-list">{lines.map((line, i) => <li key={i}>{line}</li>)}</ul> : ev.line && <p className="review-evidence">{ev.line}</p>}
        {!merged && ev.more.length > 0 && (
          <details className="review-more">
            <summary><span className="review-more-closed">{ev.moreLabel}</span><span className="review-more-open">Show fewer</span></summary>
            <ul>{ev.more.map((item, i) => <li key={i}>{item}</li>)}</ul>
          </details>
        )}
        <p className="review-rule">
          {/* The playbook by its title ("KB-029" alone said nothing); the code stays in the tooltip. */}
          {/* A rule citing the same playbook twice ("KB-029 §1 and KB-029 §8") names its title once, then the code. */}
          <span>{(() => { const named = new Set<string>(); return ruleParts(f.rule).map((part, i) => {
            if (!(i % 2)) return part;
            const first = !named.has(part); named.add(part);
            return <Link key={i} to={`/knowledge/${part}`} title={kbTitles[part] ? `${part}: ${kbTitles[part]}` : undefined}>{first ? kbTitles[part] || part : part}</Link>;
          }); })()}</span>
          {account && <span>{account}</span>}
        </p>
        <RecommendationDetails f={f} />
      </div>
      <div className="review-item-actions">
        {f.status !== "open" ? <Handled f={f} busy={busy} onUndo={onUndo} /> : (
          <>
            {f.proposal
              ? <button className="button" disabled={busy} onClick={onQueue}><Send aria-hidden="true" />Send to approvals</button>
              : <button className="button secondary" disabled={busy} onClick={onTask}><ClipboardList aria-hidden="true" />{more.length ? `Make ${more.length + 1} tasks` : "Make a task"}</button>}
            {CAMPAIGN_KINDS.has(f.kind) && <Link className="text-button" to={campaignsLink}><ArrowUpRight aria-hidden="true" />Campaigns</Link>}
            <button className="text-button" disabled={busy} onClick={onDismiss}><X aria-hidden="true" />Dismiss</button>
          </>
        )}
      </div>
    </li>
  );
}

function Handled({ f, busy, onUndo }: { f: Finding; busy: boolean; onUndo: () => void }) {
  if (f.status === "queued" && f.approval_id) return <Link to={`/approvals?record=${f.approval_id}`}>In Approvals</Link>;
  if (f.status === "tasked" && f.task_id) return <Link to={`/tasks?task=${f.task_id}`}>In the Work queue</Link>;
  return (
    <span className="review-dismissed">
      <span className="muted">{f.dismiss_reason && /^Budget raise/.test(f.dismiss_reason) ? f.dismiss_reason : `Dismissed${f.decided_by_name ? ` by ${f.decided_by_name}` : ""}`}</span>
      {canUndo(f) && <button type="button" className="text-button" disabled={busy} onClick={onUndo}>Undo</button>}
    </span>
  );
}

/** "2026-09-14 to 2026-10-13" as the page header says it: "Sep 14 to Oct 13". */
function periodText(period: string) {
  const m = /^(\d{4}-\d{2}-\d{2}) to (\d{4}-\d{2}-\d{2})$/.exec(String(period).trim());
  return m ? `${shortDate(m[1])} to ${shortDate(m[2])}` : period;
}

/** Decision detail stays beside the finding; no edit is justified by a threshold alone. */
function RecommendationDetails({ f }: { f: Finding }) {
  const review = f.evidence?.recommendation;
  if (!review) return null;
  const rows = [
    ["Evidence", review.evidence], ["Why act now", review.why_now],
    ["Leave unchanged or test", review.leave_alone], ["Expected impact", review.expected_impact],
    ["Learning, review and delivery", review.disruption], ["Missing evidence", review.data_gaps], ["Follow-up", review.follow_up],
  ];
  return <>
    <p className="review-evidence"><strong>{review.decision}</strong>{review.period ? <span className="review-period">{periodText(review.period)}</span> : null}</p>
    <details className="review-more">
      <summary>Decision and evidence</summary>
      {rows.filter(([, value]) => value).map(([label, value]) => <p key={label}><strong>{label}:</strong> {value}</p>)}
      {Array.isArray(review.sources) && <ul className="review-sources">{review.sources.map((source: { title: string; url: string }) => <li key={source.url}><a href={source.url} target="_blank" rel="noreferrer">{source.title}</a></li>)}</ul>}
    </details>
  </>;
}

/** The audit SOP in its own order, each step with what this week's review found and what a person checks by hand. */
function SopChecklist({ steps }: { steps: SopStep[] }) {
  const kbTitles = useContext(KbTitles);
  return (
    <section className="review-group review-sop-checklist" aria-labelledby="sop-checklist-title">
      <div className="review-sop-head">
        <h2 id="sop-checklist-title">SOP checklist</h2>
        <Link className="text-button" to="/sops"><ArrowUpRight aria-hidden="true" />Open the SOP</Link>
      </div>
      <ol className="review-sop-list">
        {steps.map((s) => (
          <li key={s.step} className={`review-sop-step is-${s.status}`}>
            <span className="review-sop-num">{s.step}</span>
            <div className="review-sop-main">
              <span className="review-sop-title">{s.title}</span>
              <span className="review-sop-kb">
                {ruleParts(s.kb).map((part, i) => (i % 2 ? <Link key={i} to={`/knowledge/${part}`} title={kbTitles[part] ? `${part}: ${kbTitles[part]}` : undefined}>{part}</Link> : part))}
              </span>
              {s.status === "unread" && s.failed.length > 0 && <p className="review-sop-note">Couldn't read {sopFailedText(s)} in the last review.</p>}
              {s.person && <p className="review-sop-note">{s.status === "person" ? s.person : `By hand: ${s.person}`}</p>}
            </div>
            <span className="review-sop-status">{sopStatusText(s)}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}
